import { Button, List, Stack, Text } from '@chakra-ui/react'
import { Link as RouterLink } from 'react-router'
import en from '~/i18n/en.json'
import { useT } from '~/i18n/use-t'
import { ActionBar } from '~/components/member/ActionBar'
import { WhyLink } from '~/components/member/WhyLink'

export type OutcomeKind = 'try_again' | 'not_started' | 'duplicate'

/** Hints of the API that are about the flow, not about the provider's reasons. They never get a line of their own. */
const STRUCTURAL = new Set(['not_started', 'no_decision', 'liveness_failed', 'client_error'])

/** The allowlist (R89): the codes with copy in `outcome.hint`. */
export const KNOWN_HINTS: ReadonlySet<string> = new Set(Object.keys(en.outcome.hint))

/**
 * R89: a reason line is shown only for a code this build has copy for. A code that is not known adds one generic
 * line instead (it is never printed, and never a score). At most one line is shown.
 */
export function hintLine(hints: readonly string[]): { known: string } | 'generic' | null {
  const known = hints.find((h) => KNOWN_HINTS.has(h))
  if (known) return { known }
  return hints.some((h) => !STRUCTURAL.has(h)) ? 'generic' : null
}

/**
 * S4 outcomes (copy v8): try again, the check did not start, and "a member who looks like you"; copy v11: try again
 * after our own failure (R106).
 */
export function Outcome({
  kind,
  attemptsLeft = 0,
  canStart = true,
  hints = [],
  ourSide = false,
  onTryAgain,
}: {
  kind: OutcomeKind
  attemptsLeft?: number
  canStart?: boolean
  hints?: readonly string[]
  /** R106: the provider passed the check, but our side failed; the try did not count. */
  ourSide?: boolean
  onTryAgain?: () => void
}) {
  const { t, config } = useT()
  // "Try again" is the screen's main action: in the action area like every other step's primary button.
  const tryAgain = (
    <ActionBar>
      <Button size='lg' w='full' onClick={onTryAgain}>
        {t('common.tryAgain')}
      </Button>
    </ActionBar>
  )

  if (kind === 'duplicate')
    return (
      <Stack gap={4} role='alert'>
        <Text as='h2' fontWeight='bold' fontSize='xl'>
          {t('outcome.duplicateTitle')}
        </Text>
        <Text>{t('outcome.duplicateText')}</Text>
        <List.Root ps={6} gap={3}>
          <List.Item>
            {t('outcome.duplicateJoined')}{' '}
            <Stack direction={{ base: 'column', sm: 'row' }} gap={3} mt={2} align={{ base: 'stretch', sm: 'center' }}>
              <Button asChild>
                <RouterLink to='/login'>{t('common.logIn')}</RouterLink>
              </Button>
              <Button asChild variant='outline'>
                <RouterLink to='/login'>{t('outcome.duplicateSignIn')}</RouterLink>
              </Button>
            </Stack>
          </List.Item>
          <List.Item>{t('outcome.duplicateMistake', { contact: config.org.contact })}</List.Item>
        </List.Root>
        <Text fontStyle='italic'>{t('outcome.duplicateEmail')}</Text>
      </Stack>
    )

  if (kind === 'not_started')
    return (
      <Stack gap={4} role='alert'>
        <Text>{t('outcome.notStarted')}</Text>
        {onTryAgain && tryAgain}
      </Stack>
    )

  // R106: the check went through at the provider, so no "did not work" line, no tips and no tries count.
  if (ourSide)
    return (
      <Stack gap={4} role='alert'>
        <Text as='h2' fontWeight='bold' fontSize='xl'>
          {t('outcome.ourSideTitle')}
        </Text>
        <Text>{t('outcome.ourSideText')}</Text>
        {canStart && attemptsLeft > 0 && onTryAgain && tryAgain}
      </Stack>
    )

  const line = hintLine(hints)
  return (
    <Stack gap={4} role='alert'>
      {line && line !== 'generic' && <Text fontWeight='bold'>{t(`outcome.hint.${line.known}`)}</Text>}
      {line === 'generic' && <Text fontWeight='bold'>{t('outcome.genericHint')}</Text>}
      <Text>
        {t('outcome.tryAgain')}{' '}
        {attemptsLeft > 0 ? t('outcome.tryLeft', { count: attemptsLeft }) : t('outcome.tryNone')}
      </Text>
      <WhyLink faq='cameraAllow' label={t('face.cameraHelpLink')} alignSelf='flex-start' />
      {canStart && attemptsLeft > 0 && onTryAgain && tryAgain}
    </Stack>
  )
}
